/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useContext, useState } from 'react';
import { Menu, PageHeader, Space, Typography, Col, Row, Button } from 'antd';
import {
  AndroidFilled,
  AppleFilled,
  HddFilled,
  WindowsFilled,
} from '@ant-design/icons';
import styles from './styles.module.css';
import { DeviceDetailsContext } from '../../index';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import TimeAgo from 'timeago-react';
import EditDevice from '../../../../components/DevicesTable/components/EditDevice';
import ConfigContext from '../../../../../../components/ConfigContext';
import { useTranslation } from 'react-i18next';
import 'moment-timezone';
import BreadcrumbComponent from '../../../../../../components/BreadCrumb';
import DeviceGroups from './components/DeviceGroups';

const { Title, Text } = Typography;

const routes = (deviceName, appName, t) => {
  return [
    {
      path: `/${appName}`,
      breadcrumbName: t('home_menu_home'),
    },
    {
      path: `/${appName}/devices`,
      breadcrumbName: t('home_menu_devices'),
    },
    {
      breadcrumbName: deviceName.charAt(0).toUpperCase() + deviceName.slice(1),
    },
  ];
};

const getItem = (label, key, icon, children, type) => {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
};

const Header = props => {
  const deviceDetails = useContext(DeviceDetailsContext);
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  let history = useHistory();
  let icon;
  switch (deviceDetails.deviceType) {
    case 'android':
      icon = <AndroidFilled />;
      break;
    case 'ios':
      icon = <AppleFilled />;
      break;
    case 'windows':
      icon = <WindowsFilled />;
      break;
    default:
      icon = <HddFilled />;
  }

  const items = [
    getItem(t('label_statusNinformation'), 'info'),
    getItem(t('label_operationsLog'), 'log'),
    getItem(t('label_policyCompliance'), 'policy-compliance'),
    getItem(t('label_apps'), 'apps'),
    deviceDetails.deviceType !== 'windows' &&
      deviceDetails.device.type !== 'ios' &&
      getItem(t('label_remoteSession'), 'remote-session'),
    deviceDetails.deviceType === 'windows'
      ? getItem(t('label_windowsDefender'), 'windows-defender')
      : getItem(t('label_accounts'), 'accounts'),
  ];

  const menuClick = item => {
    let path =
      `/${config.appName}/devices/${deviceDetails.deviceType}/${deviceDetails.deviceIdentifier}` +
      '/' +
      item.key;
    history.push(path);
  };

  const openModal = () => {
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
  };

  return (
    <PageHeader
      className={styles.pageHeader}
      ghost={false}
      breadcrumb={
        <BreadcrumbComponent
          breadcrumbList={routes(deviceDetails.device.name, config.appName, t)}
        />
      }
      title={
        <div>
          <Space>
            <Title level={1} className={styles.deviceName}>
              <span className={styles.deviceIcon}>{icon}</span>
              {` ${deviceDetails.device.enrolmentInfo.owner
                .charAt(0)
                .toUpperCase() +
                deviceDetails.device.enrolmentInfo.owner.slice(1)}'s ${
                deviceDetails.device.name
              }`}
            </Title>
            <EditDevice
              view={'singleDevice'}
              device={deviceDetails.device}
              callback={props.callback}
            />
          </Space>
          <div>
            {deviceDetails.device.deviceInfo.updatedTime && (
              <Text className={styles.deviceUpdatedTime}>
                {`${t('label_lastUpdated')}: ${moment
                  .utc(deviceDetails.device.deviceInfo.updatedTime)
                  .tz(moment.tz.guess())
                  .format('LLLL')} (`}
                <TimeAgo
                  datetime={moment
                    .utc(deviceDetails.device.deviceInfo.updatedTime)
                    .tz(moment.tz.guess())
                    .fromNow()}
                  locale="en_US"
                />
                {')'}
              </Text>
            )}
          </div>
        </div>
      }
    >
      <Row className={styles.descriptions}>
        <Col span={4}>
          {' '}
          <Text strong>{t('label_owner')} : </Text>
          <Text>{deviceDetails.device.enrolmentInfo.owner}</Text>
        </Col>
        <Col span={4}>
          {' '}
          <Text strong>{t('label_ownership')} : </Text>
          <Text>{deviceDetails.device.enrolmentInfo.ownership}</Text>
        </Col>
        <Col span={4}>
          {' '}
          <Text strong>{t('label_deviceType')} : </Text>
          <Text>{deviceDetails.device.type}</Text>
        </Col>
        <Col span={4}>
          {' '}
          <Text strong>{t('label_OSversion')} : </Text>
          <Text>{deviceDetails.device.deviceInfo.osVersion}</Text>
        </Col>
        <Col span={5}>
          {' '}
          <Text strong>{t('label_dateOfEnrolment')} : </Text>
          <Text>
            {moment(
              new Date(deviceDetails.device.enrolmentInfo.dateOfEnrolment),
            ).format('ddd, DD MMM YYYY')}
          </Text>
        </Col>
        <Col span={2}>
          <Button type="link" size="small" onClick={openModal}>
            {t('home_menu_groups')}
          </Button>
        </Col>
      </Row>

      <DeviceGroups
        openModal={openModal}
        hideModal={hideModal}
        isModalVisible={isModalVisible}
      />

      <Menu
        mode="horizontal"
        selectedKeys={[deviceDetails.currentRoute]}
        className={styles.menu}
        items={items}
        onClick={menuClick}
      />
    </PageHeader>
  );
};
export default Header;
